import copy from 'fast-copy'
import moment from 'moment'
import { generateQuoteReportV2, Orientation, PageSize, paperSizes } from 'pdf-generator'
import React from 'react'
import { LoadingSection, PDFBuilder, PDFGenerationConfig, renderingOptions } from '../../../components'
import { SelectBoxOption } from '../../../interfaces'
import { useAppDispatch, useAppSelector } from '../../../redux'
import { useLazyGetAttachmentUrlQuery, useUpdateQuoteMutation } from '../../../redux/services'
import { QuoteSliceInterface, updateQuoteSlice } from '../../../redux/slices/quote'
import { AppRoutes } from '../../../router/routes'
import { blobToBase64 } from '../../../utils'
import { orientationOptions } from '../../documentTemplates/dashboard/addEditTemplate'
import { preparePayload } from '../utils'


const QuoteFormat: React.FC<{ quoteId: string, readOnly?: boolean, loading?: boolean }> = ({ quoteId, readOnly, loading }) => {

    const footerURL = `${window.location.origin}${AppRoutes.viewQuickQuoteBasicDetail(quoteId)}`;

    const dispatch = useAppDispatch();
    const { quote } = useAppSelector(store => store);

    const [updateQuote] = useUpdateQuoteMutation();
    const [getAttachmentURL] = useLazyGetAttachmentUrlQuery();

    const [preparing, setPreparing] = React.useState<boolean>(true);
    const [url, setURL] = React.useState<string>('');

    const title = `${quote.name}`;

    const saveItemDetails = (newQuote: Partial<QuoteSliceInterface>) => {
        let payload = { ...preparePayload({ ...quote, ...newQuote }) };

        if (!quote.released) {
            updateQuote({ id: quoteId, payload });
        };

        dispatch(updateQuoteSlice({ ...quote, ...newQuote }));
    };

    const getURL = async (config: PDFGenerationConfig) => {
        const copyQuote = copy(quote);

        if (moment().diff(moment(copyQuote.fetched_at), 'minutes') > 2) {
            let attachmentArr: Promise<any>[] = [];

            if (quote.cover_page_config.attachments?.length > 0 && !copyQuote.cover_page_config.attachments[0].base64) {
                attachmentArr.push(getAttachmentURL({ id: quote.cover_page_config.attachments?.[0]?._id, object_type: "quote", object_id: quoteId }).unwrap().then(async (url) => {
                    copyQuote.cover_page_config.attachments[0].url = url;

                    try {
                        const base64 = await blobToBase64(url) as string;
                        copyQuote.cover_page_config.attachments[0].base64 = base64;
                        copyQuote.cover_page_config.attachments[0].url = base64;
                        dispatch(updateQuoteSlice({ ...quote, ...copyQuote }));
                    } catch (error) {
                        console.error(`Error loading quote logo on preview: ${error}`)
                    }
                }));
            };

            if (quote.cover_letter?.attachments?.length && !copyQuote.cover_letter?.attachments?.[0]?.base64) {
                attachmentArr.push(getAttachmentURL({ id: quote.cover_letter?.attachments?.[0]?._id, object_type: "quote", object_id: quoteId }).unwrap().then(async (url) => {
                    if (copyQuote.cover_letter.attachments) {
                        copyQuote.cover_letter.attachments[0].url = url;

                        try {
                            const base64 = await blobToBase64(url) as string;
                            copyQuote.cover_letter.attachments[0].base64 = base64;
                            copyQuote.cover_letter.attachments[0].url = base64;
                            dispatch(updateQuoteSlice({ ...quote, ...copyQuote }));
                        } catch (error) {
                            console.error(`Error loading cover letter on preview: ${error}`)
                        }
                    }
                }));
            };

            if (attachmentArr.length) {
                await Promise.all(attachmentArr);
            }
        }

        let url = await generateQuoteReportV2({
            title: quote.name,
            headerTitle: quote.name,
            orientation: config.orientation,
            paperSize: config.paperSize,
            quoteDetails: copyQuote,
            showFooter: quote.hfTemplate?._id ? false : config?.enableFooter ?? false,
            footerURL,
            configState: config.configState
        });

        return url;
    };

    const setReportURL = async () => {
        if (quote.id) {
            setPreparing(true);

            const url = await getURL({
                orientation: quote.hfTemplate?._id ? `${quote.orientation?.value as Orientation ?? orientationOptions[0].value as Orientation}` : quote.hfTemplate?.orientation as Orientation ?? orientationOptions[0].value as Orientation,
                paperSize: quote.hfTemplate?._id ? `${quote.paper_size?.value as PageSize ?? paperSizes[4].value as PageSize}` : quote.hfTemplate?.paper_size as PageSize ?? paperSizes[4].value as PageSize,
                enableFooter: true,
                configState: {
                    tableHeaderColor: quote.quote_report?.table_header_color ?? "d1c4e9",
                    useMBFormatter: quote.quote_report?.use_mb_formatter ?? false,
                    include_serial_num: quote?.report_config?.include_serial_num ?? false,
                    include_revision_table: quote?.report_config?.include_revision_table ?? false,
                }
            });

            setURL(url);
            setPreparing(false);
        }
    };

    React.useEffect(() => {
        setReportURL();
        // eslint-disable-next-line
    }, []);

    if (loading || preparing) {
        return <LoadingSection message='Preparing Quote Preview...' />
    }

    return (
        <PDFBuilder
            customizeConfig={{
                includeConfig: true,
                config: {
                    includeMBFormatter: { include: true, value: quote.quote_report?.use_mb_formatter ?? false },
                    includeTableHeaderColor: { include: true, value: quote.quote_report?.table_header_color ?? "d1c4e9" },
                    include_serial_num: { include: true, value: quote?.report_config?.include_serial_num ?? false },
                    include_revision_table: { include: true, value: quote?.report_config?.include_revision_table ?? false },
                }
            }}
            url={url}
            generatePDF={getURL}
            title={title}
            disableUpdatingConfig={quote.released || readOnly}
            selectedSize={quote.paper_size}
            selectedOrientation={quote.orientation}
            selectedTemplate={quote.hfTemplate as SelectBoxOption}
            renderOption={quote.hfTemplate ? renderingOptions[1].value : renderingOptions[0].value}
            footerURL={footerURL}
            onConfigChange={(configState: any) => {
                saveItemDetails({
                    report_config: { include_serial_num: configState?.include_serial_num || false, include_revision_table: configState?.include_revision_table || false },
                    quote_report: {
                        ...quote.quote_report,
                        name: quote.quote_report?.name ?? "",
                        use_mb_formatter: configState.useMBFormatter,
                        table_header_color: configState.tableHeaderColor
                    }
                })
            }}
            onSizeChange={(paper_size) => saveItemDetails({ paper_size })}
            onOrientationChange={(orientation) => saveItemDetails({ orientation })}
            onTemplateChange={(hfTemplate) => saveItemDetails({ hfTemplate: hfTemplate as SelectBoxOption | null })}
            infoFooter={`Report generated in timezone: ${quote.time_zone}`}
        />
    )
}

export default QuoteFormat